import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { cn } from '@workspace/ui/lib/utils';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { LoginLayout } from '~/components/layouts/login-layout';
import { m } from '~/paraglide/messages.js';
import { ActivateYourCardComponent } from './-components/activate-your-card';
import { AddYourTeamComponent } from './-components/add-your-team';
import { BusinessAddressComponent } from './-components/business-address';
import { StepIndicator } from './-components/step-indicator';
import { TypeOfOrganizationComponent } from './-components/type-of-organization';
import { type BusinessType, WelcomeComponent } from './-components/welcome';
import { WhereAreYouStartComponent } from './-components/where-are-you-start';
import { useOrganizationForm } from './-hooks/use-organization-form';
import { useOrganizationSteps } from './-hooks/use-organization-steps';

export const Route = createFileRoute('/_authenticated/organization/')({
  beforeLoad: async ({ context }) => {
    if (context.auth.user?.metadata?.organization) {
      throw redirect({
        to: '/',
        replace: true,
      });
    }
  },
  component: OrganizationPage,
});

type Step = {
  id: number;
  title: string;
  description: string;
  goBack?: boolean;
  hash?: 'add-your-team' | 'add-your-card';
  component: (props: StepComponentProps) => ReactNode;
};

type StepComponentProps = {
  formData: ReturnType<typeof useOrganizationForm>['formData'];
  updateStepData: ReturnType<typeof useOrganizationForm>['updateStepData'];
  handleNextStep: () => void;
  handleBusinessTypeSelection: (type: BusinessType) => void;
  handleVerifyIdentity: () => void;
  handleSkipActivateCard: () => void;
  businessType: 'from-scratch' | 'existing' | null;
};

type StepsConfig = {
  'from-scratch': Step[];
  existing: Step[];
};

const STEPS_CONFIG: StepsConfig = {
  'from-scratch': [
    {
      id: 1,
      title: m['organization.steps.welcome_title'](),
      description: m['organization.steps.welcome_description'](),
      component: ({ handleBusinessTypeSelection }) => (
        <WelcomeComponent onNext={handleBusinessTypeSelection} />
      ),
    },
    {
      id: 2,
      title: '¿Dónde vas a empezar?',
      description:
        'Sin importar donde esté tu empresa, nosotros te volvemos globales',
      goBack: true,
      component: ({ formData, updateStepData, handleNextStep }) => (
        <WhereAreYouStartComponent
          values={formData.whereAreYouStart}
          onUpdate={(data) => updateStepData('whereAreYouStart', data)}
          onNext={handleNextStep}
        />
      ),
    },
    {
      id: 3,
      title: m['organization.steps.org_type_title'](),
      description: m['organization.steps.org_type_description'](),
      goBack: true,
      component: ({ formData, updateStepData, handleNextStep }) => (
        <TypeOfOrganizationComponent
          whereAreYouStart={formData.whereAreYouStart.name}
          values={formData.typeOfOrganization}
          onUpdate={(data) => updateStepData('typeOfOrganization', data)}
          onNext={handleNextStep}
        />
      ),
    },
    {
      id: 4,
      title: m['organization.steps.business_info_title'](),
      description: m['organization.steps.business_info_description'](),
      goBack: true,
      component: ({
        formData,
        updateStepData,
        handleNextStep,
        businessType,
      }) => (
        <BusinessAddressComponent
          values={formData.businessAddress}
          onUpdate={(data) => updateStepData('businessAddress', data)}
          onNext={handleNextStep}
          businessType={businessType || undefined}
        />
      ),
    },
    {
      id: 5,
      title: m['organization.steps.add_team_title'](),
      description: m['organization.steps.add_team_description'](),
      goBack: true,
      hash: 'add-your-team',
      component: ({ formData, updateStepData, handleNextStep }) => (
        <AddYourTeamComponent
          values={formData.teamMembers}
          onUpdate={(data) => updateStepData('teamMembers', data)}
          onNext={handleNextStep}
        />
      ),
    },
    {
      id: 6,
      title: m['organization.steps.activate_card_title'](),
      description: m['organization.steps.activate_card_description'](),
      goBack: true,
      component: ({ handleVerifyIdentity, handleSkipActivateCard }) => (
        <ActivateYourCardComponent
          onNext={handleVerifyIdentity}
          onSkip={handleSkipActivateCard}
        />
      ),
    },
  ],
  existing: [
    {
      id: 1,
      title: m['organization.steps.welcome_title'](),
      description: m['organization.steps.welcome_description'](),
      component: ({ handleBusinessTypeSelection }) => (
        <WelcomeComponent onNext={handleBusinessTypeSelection} />
      ),
    },
    {
      id: 2,
      title: '¿Dónde vas a empezar?',
      description:
        'Sin importar donde esté tu empresa, nosotros te volvemos globales',
      goBack: true,
      component: ({ formData, updateStepData, handleNextStep }) => (
        <WhereAreYouStartComponent
          values={formData.whereAreYouStart}
          onUpdate={(data) => updateStepData('whereAreYouStart', data)}
          onNext={handleNextStep}
        />
      ),
    },
    {
      id: 3,
      title: m['organization.steps.existing_business_title'](),
      description: m['organization.steps.existing_business_description'](),
      goBack: true,
      component: ({
        formData,
        updateStepData,
        handleNextStep,
        businessType,
      }) => (
        <BusinessAddressComponent
          values={formData.businessAddress}
          onUpdate={(data) => updateStepData('businessAddress', data)}
          onNext={handleNextStep}
          businessType={businessType || undefined}
        />
      ),
    },
    {
      id: 4,
      title: m['organization.steps.add_team_title'](),
      description: m['organization.steps.add_team_description'](),
      goBack: true,
      hash: 'add-your-team',
      component: ({ formData, updateStepData, handleNextStep }) => (
        <AddYourTeamComponent
          values={formData.teamMembers}
          onUpdate={(data) => updateStepData('teamMembers', data)}
          onNext={handleNextStep}
        />
      ),
    },
    {
      id: 5,
      title: m['organization.steps.activate_card_title'](),
      description: m['organization.steps.activate_card_description'](),
      goBack: true,
      component: ({ handleVerifyIdentity, handleSkipActivateCard }) => (
        <ActivateYourCardComponent
          onNext={handleVerifyIdentity}
          onSkip={handleSkipActivateCard}
        />
      ),
    },
  ],
};

function OrganizationPage() {
  const navigate = useNavigate();

  const {
    formData,
    updateStepData,
    businessType,
    setBusinessType,
    createOrganization,
  } = useOrganizationForm();

  const steps = businessType
    ? STEPS_CONFIG[businessType]
    : STEPS_CONFIG.existing;

  const { currentStep, handleNextStep, handlePrevStep } = useOrganizationSteps(
    steps.length,
  );

  const handleBusinessTypeSelection = (type: BusinessType) => {
    setBusinessType(type);
    handleNextStep();
  };

  const currentStepConfig = steps[currentStep - 1];

  const handleVerifyIdentity = async () => {
    await createOrganization();
    setTimeout(() => {
      navigate({ to: '/verify-identity/kyc' });
    }, 200);
  };

  const handleSkipActivateCard = async () => {
    await createOrganization();
    setTimeout(() => {
      navigate({ to: '/' });
    }, 200);
  };

  const renderStepComponent = () => {
    const step = steps[currentStep - 1];

    if (!step) return null;

    return step.component({
      formData,
      updateStepData,
      handleNextStep,
      handleBusinessTypeSelection,
      handleVerifyIdentity,
      handleSkipActivateCard,
      businessType,
    });
  };

  return (
    <LoginLayout>
      <StepIndicator steps={steps} currentStep={currentStep} />

      <div className="flex min-h-[80vh] w-full flex-col items-center justify-center">
        <div className="mb-6 w-full max-w-md">
          <div className="mb-4 flex items-center justify-between">
            {currentStepConfig.goBack && (
              <button
                type="button"
                onClick={handlePrevStep}
                className="flex items-center gap-2 rounded px-2 py-1 text-gray-400 transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm">
                  {m['organization.steps.back']()}
                </span>
              </button>
            )}

            {currentStepConfig.hash === 'add-your-card' && (
              <button
                type="button"
                onClick={handlePrevStep}
                className="flex items-center gap-2 rounded px-2 py-1 text-gray-400 transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <span className="text-sm">
                  {m['organization.steps.finish']()}
                </span>
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {currentStepConfig.title}
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              {currentStepConfig.description}
            </p>
          </div>
        </div>

        <div className="flex w-full justify-center">
          <div
            className={cn('w-full space-y-6', {
              'max-w-md': !(
                currentStepConfig?.hash === 'add-your-team' ||
                currentStepConfig?.hash === 'add-your-card'
              ),
            })}
          >
            {renderStepComponent()}
          </div>
        </div>
      </div>
    </LoginLayout>
  );
}
